<template>
  <div class="h-full" @click="resetInactivity" @keydown="resetInactivity">
    <AppBotLoader v-if="!isMaintenance && user" @onBotLoaded="handleBotLoaded()" />
    <router-view v-if="isBotLoaded || isPublic" v-slot="{ Component, route: viewRoute }">
      <!-- A lead only gets a few views, the others show the demo request (see `leadRestriction.ts`) -->
      <LeadLockedView v-if="isLocked(viewRoute)" />
      <component :is="Component" v-else />
    </router-view>
    <HubFaviconIncome v-if="!isMaintenance && currentBot" />

    <SnackbarStack />

    <!-- NPS Modal -->
    <NpsModal v-model="npsSurveyOpen" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import moment from 'moment';
import { messageTypes, styleTypes } from '@/utils/notifications';
import AppBotLoader from '@/components/app/AppBotLoader.vue';
import HubFaviconIncome from '@/components/hub/HubFaviconIncome.vue';
import SnackbarStack from '@/components/tools/SnackbarStack.vue';
import NpsModal from '@/components/nps/NpsModal.vue';
import LeadLockedView from '@/components/leadBot/LeadLockedView.vue';
import { useLeadRestriction } from '@/components/leadBot/leadRestriction';
import NpsService from '@/services/nps';
import i18n from '@/i18n';
import { FeatureEnum } from '@/utils/userRoles';
import { useAppStore } from '@/stores/app';
import { useHubUiStore } from '@/stores/hubUi';
import { useAuthStore } from '@/stores/auth';
import { useLivechatStore } from '@/stores/livechat';
import { useBotsStore } from '@/stores/bots';
import { useContentStore } from '@/stores/content';
import { useAudioStore } from '@/stores/audio';
import { useInactivityTrackerStore } from '@/stores/inactivityTracker';
import { useNotificationsStore } from '@/stores/notifications';

defineOptions({
  inheritAttrs: false,
});

type Bot = {
  uniqueBotId?: string;
  stagingBot?: string;
};

const appStore = useAppStore();
const authStore = useAuthStore();
const livechatStore = useLivechatStore();
const botsStore = useBotsStore();
const contentStore = useContentStore();
const audioStore = useAudioStore();
const inactivityTrackerStore = useInactivityTrackerStore();
const notificationsStore = useNotificationsStore();
const hubUiStore = useHubUiStore();
const route = useRoute();
const router = useRouter();

const isBotLoaded = ref(false);
const { isLocked, isRestricted } = useLeadRestriction();

/**
 * Whether the dashboard is where the user landed, rather than opened on purpose from within the Hub: on a page load,
 * from the login (or 2FA, SSO, password) pages, or through the start page `/`.
 * Not only through `/`: logged out, `/` already became `/welcome` before the login, which then leads there directly.
 */
const landedOnDashboard = ref(route.name === 'welcome');
const removeLandingHook = router.afterEach((to, from) => {
  // E.g. only the query changed
  if (to.name === from.name) return;

  const fromOutsideHub = !from.name || from.matched.some((record) => record.meta.onlyWhenLoggedOut);
  landedOnDashboard.value = to.name === 'welcome' && (fromOutsideHub || to.redirectedFrom?.path === '/');
});
onBeforeUnmount(removeLandingHook);

/**
 * A lead starts in the Playground: the dashboard, where the Hub lands them, is locked for them. Opening the
 * dashboard on purpose still shows the demo request. Waits for the bot, which tells the stage.
 */
watch(
  [isRestricted, landedOnDashboard, () => route.name],
  ([restricted, landed]) => {
    if (restricted && landed && route.name === 'welcome') {
      landedOnDashboard.value = false;
      router.replace({ name: 'ai' });
    }
  },
  { immediate: true },
);

let checkInterval: ReturnType<typeof setInterval> | null = null;

const user = computed(() => authStore.userGetter);
const isAdmin = computed(() => authStore.isAdmin);
const isTester = computed(() => authStore.isTester);
const isOwner = computed(() => authStore.isOwner);
const activeHubSettings = computed(() => livechatStore.activeHubSettings);
const currentLicence = computed(() => botsStore.currentLicence);
const bots = computed(() => botsStore.botsGetter);
const currentBotId = computed(() => botsStore.currentBotId);
const currentBot = computed(() => botsStore.currentBotGetter);
const isDeployable = computed(() => contentStore.isDeployable);
const lastChangeDate = computed(() => contentStore.lastChangeDate);

const isMaintenance = computed(() => import.meta.env.VITE_MAINTENANCE === 'true');
const isDemoLicence = computed(() => currentLicence.value?.package === 'demo' || false);
const isPublic = computed(() => !!route.meta?.public);

/**
 * Below this viewport width the 0–10 NPS scale doesn't fit, so we don't ask.
 * Deliberately not one of the Tailwind breakpoints — it's about the scale's
 * intrinsic width, not the page layout.
 */
const NPS_MIN_VIEWPORT_WIDTH = 720;

const npsSurveyOpen = computed({
  get: () => hubUiStore.npsSurvey.open,
  set: (value: boolean) => {
    if (value) hubUiStore.openNPSSurvey();
    else hubUiStore.closeNPSSurvey();
  },
});

/**
 * Ask the backend whether this user is due an NPS survey. A positive answer
 * only *arms* it — the router shows it on the next navigation.
 */
const checkNpsPrompt = async () => {
  // Don't show NPS below 720px — scale doesn't fit
  if (window.innerWidth < NPS_MIN_VIEWPORT_WIDTH) {
    return;
  }

  // Phase 1: only Owners are eligible
  if (!user.value || !isOwner.value) {
    return;
  }

  // Don't check if no bot is loaded yet
  if (!currentBotId.value) {
    return;
  }

  try {
    const result = await NpsService.checkPromptEligibility(currentBotId.value);
    hubUiStore.armNPSSurvey(result.shouldShow);
  } catch (error) {
    // Fail silently - don't block user experience
    console.error('NPS check failed:', error);
  }
};

const getNavigatorLang = () => {
  const lang = navigator.language || (navigator as Navigator & { userLanguage?: string }).userLanguage || 'en';
  if (lang.includes('de')) return 'de';
  if (lang.includes('es')) return 'es';
  return 'en';
};

const ensureAudio = () => {
  if (!activeHubSettings.value?.notificationAudio) return;
  return audioStore.initAudioContext();
};

const killAudio = () => {
  audioStore.purgeAudioSystem();
};

const resetInactivity = () => {
  inactivityTrackerStore.restartTimeoutTimer();
};

const versionCheck = () => {
  appStore.checkVersion();
};

const setBotIdQuery = async () => {
  if (!currentBotId.value || !isAdmin.value || route.query.bid) {
    return;
  }
  const query = { ...route.query, bid: currentBotId.value };
  // Keep the hash: e.g. the demo status page's hand-off ID lives there
  await router.replace({ query, hash: route.hash }).catch(() => true);
};

const setDemoNotification = () => {
  const name = 'permanent-demo';
  if (isDemoLicence.value) {
    notificationsStore.addNotification({
      name,
      styleType: styleTypes.INFO,
      messageType: messageTypes.DEMO_ACCOUNT,
      permanent: true,
    });
  } else {
    notificationsStore.removeNotification(name);
  }
};

const goToPreview = () => {
  const bot = bots.value?.find((b: Bot) => b.uniqueBotId === (currentBotId.value || bots.value?.[0]?.uniqueBotId || false));
  if (!bot) return;

  if (bot.stagingBot && (route.fullPath.includes('/preview/') || route.fullPath.includes('%2Fpreview%2F'))) {
    return;
  }
  router.push(`/preview/${bot.stagingBot}`);
};

const handleLivechatInit = () => {
  if (user.value?.features?.includes(FeatureEnum.LIVECHAT)) {
    livechatStore.init({
      botId: currentBotId.value,
      routePath: route.path,
    });

    window.addEventListener('beforeunload', () => {
      livechatStore.registerTabClosed();
    });

    document.addEventListener('visibilitychange', () => {
      livechatStore.handleVisibilityChange();
    });
  } else {
    livechatStore.setAgent(null);
  }
};

const handleBotLoaded = async () => {
  console.log('Bot loaded');
  setDemoNotification();
  await setBotIdQuery();
  handleLivechatInit();
  isBotLoaded.value = true;

  // Check for NPS prompt eligibility after bot is loaded
  checkNpsPrompt();
};

const init = async () => {
  if (import.meta.env.VITE_MAINTENANCE === 'true') return;

  await authStore.getUser({ isPublic: route.meta?.public });
  notificationsStore.clearNotifications();

  const favicon = document.querySelector<HTMLLinkElement>('link[rel*="icon"]');
  if (favicon) favicon.href = '/favicon.png';

  if (!user.value) {
    const lang = getNavigatorLang();
    i18n.global.locale.value = lang;
  }

  if (isTester.value) {
    goToPreview();
    return;
  }

  checkInterval = setInterval(versionCheck, 1000 * 60 * 10);
  versionCheck();
};

onMounted(() => {
  init();
  document.addEventListener('click', ensureAudio);
  document.addEventListener('focus', ensureAudio);
});

onBeforeUnmount(() => {
  if (checkInterval) clearInterval(checkInterval);
  document.removeEventListener('click', ensureAudio);
  document.removeEventListener('focus', ensureAudio);
  audioStore.purgeAudioSystem();
});

watch(user, (newUser) => {
  if (!newUser) return;

  if (!newUser.language) {
    authStore.saveLanguage(getNavigatorLang());
  } else {
    i18n.global.locale.value = newUser.language;
  }

  if (isTester.value) {
    goToPreview();
  }
});

watch(isDemoLicence, () => {
  setDemoNotification();
});

watch(() => route.fullPath, (to, from) => {
  setBotIdQuery();
  notificationsStore.clearRouteNotifications({
    routeName: route.name,
    fullPath: route.fullPath,
  });
});

watch(() => activeHubSettings.value?.notificationAudio, (state) => {
  if (state === undefined) return;
  if (state) ensureAudio();
  else killAudio();
});
</script>
